import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, RotateCcw } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import AdminPageHeader from "./AdminPageHeader";

/**
 * Admin → Service Lines (Nana Oct-2026 revision).
 *
 * Changes from v1:
 *  • Loads with ?include_inactive=true so admins can see dormant rows and
 *    reactivate them (the prior version hid inactive rows outright, which
 *    is exactly why FH/GHI appeared "missing" when they were deactivated
 *    on prod and the admin had no visible row to flip back on).
 *  • Inactive rows render with a muted background so state is obvious at
 *    a glance; a "Show inactive" filter lets admins hide them again.
 *  • Add-service-line now inspects the structured 409 body: when the
 *    duplicate code is INACTIVE, we offer a 1-click "Reactivate it
 *    instead" CTA rather than making the admin hunt the dormant row.
 *  • Deactivate flow understands the "ACTIVE_CASES_ATTACHED" guard and
 *    surfaces a confirmation dialog with the open-case count + force
 *    option; existing cases keep their service_line value either way.
 */
export default function ServiceLines() {
  const { readOnly } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState({ code: "", name: "", headcount: 0 });
  const [showInactive, setShowInactive] = useState(true);

  // Dialog state for "code exists but deactivated — reactivate instead?"
  const [reactivateDlg, setReactivateDlg] = useState(null);
  // Dialog state for "code is active and still has open cases — force deactivate?"
  const [forceDeactivateDlg, setForceDeactivateDlg] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/service-lines", {
        params: { include_inactive: true },
      });
      setRows(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const visibleRows = useMemo(() => {
    const list = showInactive ? rows : rows.filter((r) => r.active !== false);
    // Active rows first, then inactive at the bottom (visually dimmed).
    return [...list].sort((a, b) => {
      const aA = a.active !== false ? 0 : 1;
      const bA = b.active !== false ? 0 : 1;
      if (aA !== bA) return aA - bA;
      return (a.order ?? 0) - (b.order ?? 0);
    });
  }, [rows, showInactive]);

  const patch = async (id, body, { force = false } = {}) => {
    if (readOnly) return;
    try {
      await api.patch(`/admin/service-lines/${id}`, body, {
        params: force ? { force: true } : undefined,
      });
      await load();
      return true;
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Guard: deactivation blocked by open cases → prompt for ?force=true.
      if (
        detail &&
        typeof detail === "object" &&
        detail.code === "ACTIVE_CASES_ATTACHED" &&
        body?.active === false
      ) {
        setForceDeactivateDlg({
          id,
          code: detail.service_line_code,
          openCount: detail.open_case_count,
        });
        return false;
      }
      toast.error(formatApiErrorDetail(detail) || err.message);
      return false;
    }
  };

  const add = async () => {
    if (readOnly || !adding.code.trim() || !adding.name.trim()) return;
    try {
      await api.post("/admin/service-lines", {
        code: adding.code.trim().toUpperCase(),
        name: adding.name.trim(),
        headcount: Number(adding.headcount) || 0,
        active: true,
      });
      toast.success("Service line added");
      setAdding({ code: "", name: "", headcount: 0 });
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Structured 409 → offer the reactivate shortcut inline.
      if (detail && typeof detail === "object" && detail.code === "EXISTS_INACTIVE") {
        setReactivateDlg({
          code: adding.code.trim().toUpperCase(),
          existing_id: detail.existing_id,
          existing_name: detail.existing_name,
          desired_name: adding.name.trim(),
          desired_headcount: Number(adding.headcount) || 0,
        });
        return;
      }
      toast.error(formatApiErrorDetail(detail) || err.message);
    }
  };

  const confirmReactivate = async () => {
    if (!reactivateDlg) return;
    const { existing_id, desired_name, desired_headcount, code } = reactivateDlg;
    // Reactivate + sync the name/headcount the admin just typed, so the
    // reactivation feels equivalent to a fresh create.
    const body = { active: true };
    if (desired_name && desired_name !== reactivateDlg.existing_name) body.name = desired_name;
    if (desired_headcount !== undefined) body.headcount = desired_headcount;
    const ok = await patch(existing_id, body);
    if (ok) {
      toast.success(`Service line ${code} reactivated`);
      setAdding({ code: "", name: "", headcount: 0 });
    }
    setReactivateDlg(null);
  };

  const confirmForceDeactivate = async () => {
    if (!forceDeactivateDlg) return;
    const { id, code, openCount } = forceDeactivateDlg;
    const ok = await patch(id, { active: false }, { force: true });
    if (ok) toast.success(`${code} deactivated. ${openCount} open case(s) kept their service line value.`);
    setForceDeactivateDlg(null);
  };

  return (
    <div data-testid="admin-service-lines-page" className="space-y-5">
      <AdminPageHeader
        eyebrow="Admin · Service lines"
        title="Service lines"
        description="Goodwin Living service-line directory. Add new codes here and they're immediately usable across team members, cases, dashboards, and CSV imports — no code deploy required. Headcount feeds dashboard case-rate normalization."
      />
      {!readOnly && (
        <div className="brand-card p-4">
          <h2 className="text-sm font-semibold">Add service line</h2>
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <Input
              value={adding.code}
              onChange={(e) => setAdding((a) => ({ ...a, code: e.target.value }))}
              placeholder="Code (e.g. NEW)"
              className="w-[120px]"
              aria-label="Service line code"
              data-testid="service-line-add-code"
            />
            <Input
              value={adding.name}
              onChange={(e) => setAdding((a) => ({ ...a, name: e.target.value }))}
              placeholder="Display name"
              className="w-[260px]"
              aria-label="Service line name"
              data-testid="service-line-add-name"
            />
            <Input
              type="number"
              value={adding.headcount}
              onChange={(e) => setAdding((a) => ({ ...a, headcount: e.target.value }))}
              className="w-[120px]"
              aria-label="Headcount"
              data-testid="service-line-add-headcount"
              min={0}
            />
            <Button
              data-testid="service-line-add-submit"
              onClick={add}
              className="goodwin-primary-btn text-white"
            >
              <Plus className="mr-1 h-4 w-4" />
              Add
            </Button>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-[color:var(--page-fg-muted)]">
          <Checkbox
            checked={showInactive}
            onCheckedChange={(v) => setShowInactive(!!v)}
            data-testid="service-line-show-inactive"
            aria-label="Show inactive service lines"
          />
          Show inactive
        </label>
        <span className="text-xs text-[color:var(--page-fg-muted)]">
          {rows.filter((r) => r.active !== false).length} active ·{" "}
          {rows.filter((r) => r.active === false).length} inactive
        </span>
      </div>
      <div className="brand-card overflow-hidden">
        <table className="w-full text-sm">
          <caption className="sr-only">Service lines</caption>
          <thead className="bg-[color:var(--surface-2)] text-left text-xs uppercase tracking-wider text-[color:var(--page-fg-muted)]">
            <tr>
              <th className="px-4 py-3" scope="col">Code</th>
              <th className="px-4 py-3" scope="col">Name</th>
              <th className="px-4 py-3" scope="col">Headcount</th>
              <th className="px-4 py-3" scope="col">Active</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[color:var(--border-subtle)]">
            {visibleRows.map((r) => {
              const inactive = r.active === false;
              return (
                <tr
                  key={r.id}
                  data-testid={`service-line-row-${r.code}`}
                  data-active={inactive ? "false" : "true"}
                  className={inactive ? "bg-[color:var(--surface-2)] opacity-70" : ""}
                >
                  <td className="px-4 py-3 font-mono text-xs text-[color:var(--page-fg)]">
                    {r.code}
                    {inactive && (
                      <span className="ml-2 rounded bg-[color:var(--surface-3,#e5e7eb)] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-[color:var(--page-fg-muted)]">
                        inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[color:var(--page-fg)]">{r.name}</td>
                  <td className="px-4 py-3">
                    <Input
                      type="number"
                      min={0}
                      defaultValue={r.headcount}
                      disabled={readOnly}
                      className="h-8 w-24"
                      onBlur={(e) =>
                        Number(e.target.value) !== r.headcount &&
                        patch(r.id, { headcount: Number(e.target.value) })
                      }
                      aria-label={`${r.code} headcount`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={!inactive}
                        onCheckedChange={(v) => patch(r.id, { active: v })}
                        disabled={readOnly}
                        aria-label={`Toggle ${r.code} active`}
                        data-testid={`service-line-toggle-${r.code}`}
                      />
                      {inactive && !readOnly && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => patch(r.id, { active: true })}
                          data-testid={`service-line-reactivate-${r.code}`}
                        >
                          <RotateCcw className="mr-1 h-3.5 w-3.5" />
                          Reactivate
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ───── Reactivate-instead-of-create dialog ───── */}
      <AlertDialog open={!!reactivateDlg} onOpenChange={(o) => !o && setReactivateDlg(null)}>
        <AlertDialogContent data-testid="service-line-reactivate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reactivate existing service line?</AlertDialogTitle>
            <AlertDialogDescription>
              {reactivateDlg && (
                <>
                  Service line{" "}
                  <span className="font-mono font-semibold">{reactivateDlg.code}</span> already
                  exists under the name{" "}
                  <span className="font-medium">{reactivateDlg.existing_name}</span>, but it's
                  currently deactivated. Reactivate it instead of creating a duplicate?
                  {reactivateDlg.desired_name &&
                    reactivateDlg.desired_name !== reactivateDlg.existing_name && (
                      <div className="mt-2 text-sm">
                        Name will also be updated to:{" "}
                        <span className="font-medium">{reactivateDlg.desired_name}</span>
                      </div>
                    )}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="service-line-reactivate-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmReactivate}
              data-testid="service-line-reactivate-confirm"
            >
              Reactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ───── Force-deactivate (open cases attached) dialog ───── */}
      <AlertDialog
        open={!!forceDeactivateDlg}
        onOpenChange={(o) => !o && setForceDeactivateDlg(null)}
      >
        <AlertDialogContent data-testid="service-line-force-deactivate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate with open cases attached?</AlertDialogTitle>
            <AlertDialogDescription>
              {forceDeactivateDlg && (
                <>
                  <span className="font-mono font-semibold">
                    {forceDeactivateDlg.code}
                  </span>{" "}
                  still has{" "}
                  <span className="font-semibold">
                    {forceDeactivateDlg.openCount} open case
                    {forceDeactivateDlg.openCount === 1 ? "" : "s"}
                  </span>{" "}
                  attached. Deactivating hides it from new-case dropdowns, but existing cases
                  keep their service-line value and continue to appear in filters and reports.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="service-line-force-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmForceDeactivate}
              data-testid="service-line-force-confirm"
            >
              Deactivate anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
